import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
  ID_TOKEN_KEY,
  LocalStorageService,
  SessionStorageService,
  SHARE_ACCESS_TOKEN_KEY,
} from '@ui';
import {
  SHARED_AUTH_SESSION_MANAGER,
  SHARED_INVALID_SESSION_HANDLER,
} from './share-auth-session.types';

export const BYPASS_AUTHENTICATION_TOKEN = new HttpContextToken<boolean>(() => false);
export const BYPASS_TOKEN_REFRESH_CHECK = new HttpContextToken<boolean>(() => false);

const AUTH_SESSION_INVALID_ERROR_CODE = 'AUTH_SESSION_INVALID';

export const sharedAuthenticationInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const localStorageService = inject(LocalStorageService);
  const sessionStorageService = inject(SessionStorageService);
  const sessionManager = inject(SHARED_AUTH_SESSION_MANAGER, { optional: true });
  const invalidSessionHandler = inject(SHARED_INVALID_SESSION_HANDLER, { optional: true });

  if (req.context.get(BYPASS_AUTHENTICATION_TOKEN) === true) {
    return next(req);
  }

  const resolveAuthorizationToken = async (): Promise<string | null | undefined> => {
    if (req.params.get('sharedToken')) {
      return sessionStorageService.getItem(SHARE_ACCESS_TOKEN_KEY);
    }

    const idToken = localStorageService.getItemWithCheck(ID_TOKEN_KEY);
    if (!idToken) {
      return null;
    }

    if (
      sessionManager
      && req.context.get(BYPASS_TOKEN_REFRESH_CHECK) !== true
    ) {
      await sessionManager.ensureValidSession({
        allowBackgroundRefresh: true,
        isSilentRefresh: true,
      });
    }

    return localStorageService.getItemWithCheck(ID_TOKEN_KEY);
  };

  return from(resolveAuthorizationToken()).pipe(
    switchMap((token) => {
      if (!token) {
        return next(req);
      }

      const cloned = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${token}`),
      });

      return next(cloned).pipe(
        catchError((error: unknown) => {
          if (
            error instanceof HttpErrorResponse
            && error.status === 401
            && error.error?.code === AUTH_SESSION_INVALID_ERROR_CODE
          ) {
            invalidSessionHandler?.handleInvalidSession();
          }

          return throwError(() => error);
        }),
      );
    }),
  );
};
